import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  OutlinedInput,
  ListItemText,
  Chip,
  Stack,
  Tabs,
  Tab,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import Typography from '@mui/material/Typography'
import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { Button } from 'reactstrap'
import { APPLICANTS_API } from '../../../../utils/consts'
import Position from './Position'
import * as Yup from 'yup'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
  backgroundColor: 'white',
  padding: '28px 32px',
  gap: '20px',
  width: '90%',
  maxWidth: '560px',
  maxHeight: '90vh',
  overflowY: 'auto',
  borderRadius: '12px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
}

// Predefined locations (kept in sync with the applicants LocationFilter list)
export const locations = ['Prishtinë HQ', 'Ferizaj', 'Vushtrri', 'Prizren', 'HO-Prishtinë', 'HO-Ferizaj', 'HO-Vushtrri', 'HO-Prizren']

export const languages = { al: 'Shqip', en: 'Angleze', de: 'Gjermane', fr: 'Frenge', it: 'Italine' }
const langKeys = Object.keys(languages)

const emptyPerLanguage = () => Object.fromEntries(langKeys.map((key) => [key, '']))

const defaultJobTitles = emptyPerLanguage()
const defaultDescriptions = emptyPerLanguage()

// Per-tab "update all languages" flags. When a tab's flag is on, editing it
// re-translates every other language from it; when it is off the edit stays
// local to that tab. A new position defaults to translating from Albanian (the
// source language), while editing an existing one defaults to fully manual so
// that touching one tab can never silently rewrite translations already saved.
const noLanguageSync = () => Object.fromEntries(langKeys.map((key) => [key, false]))
const defaultUpdateAll = { ...noLanguageSync(), al: true }
const defaultEditUpdateAll = noLanguageSync()
const defaultErrors = { ...emptyPerLanguage(), location: '', description: emptyPerLanguage() }

const validationSchema = Yup.object().shape({
  al: Yup.string().required('Ju lutemi shenoni poziten ne Shqip'),
  de: Yup.string().required('Ju lutemi shenoni poziten ne Gjermanisht'),
  fr: Yup.string().required('Ju lutemi shenoni poziten ne Frengjisht'),
  it: Yup.string().required('Ju lutemi shenoni poziten ne Italisht'),
  en: Yup.string().required('Ju lutemi shenoni poziten ne Anglisht'),
  location: Yup.array().min(1, 'Ju lutemi zgjidhni lokacionin'),
  description: Yup.object().shape({
    al: Yup.string().required('Ju lutemi shenoni pershkrimin ne Shqip'),
    de: Yup.string().required('Ju lutemi shenoni pershkrimin ne Gjermanisht'),
    fr: Yup.string().required('Ju lutemi shenoni pershkrimin ne Frengjisht'),
    it: Yup.string().required('Ju lutemi shenoni pershkrimin ne Italisht'),
    en: Yup.string().required('Ju lutemi shenoni pershkrimin ne Anglisht')
  })
})

const OpenPosition = () => {
  const [jobPositions, setJobPositions] = useState([])
  const [jobTitles, setJobTitles] = useState(defaultJobTitles)
  const [location, setLocation] = useState([])
  const [tags, setTags] = useState([])
  const [tagInput, setTagInput] = useState('')
  const [descriptions, setDescriptions] = useState(defaultDescriptions)
  const [error, setError] = useState(defaultErrors)
  const [open, setOpen] = useState(false)
  const [editingPosition, setEditingPosition] = useState(null)
  const [editJobTitles, setEditJobTitles] = useState(defaultJobTitles)
  const [editLocation, setEditLocation] = useState([])
  const [editTags, setEditTags] = useState([])
  const [editTagInput, setEditTagInput] = useState('')
  const [editDescriptions, setEditDescriptions] = useState(defaultDescriptions)
  const [editError, setEditError] = useState(defaultErrors)
  const [editActiveTab, setEditActiveTab] = useState(0)
  const [activeTab, setActiveTab] = useState(0)
  const [isTranslating, setIsTranslating] = useState(false)
  const [isEditTranslating, setIsEditTranslating] = useState(false)
  const [updateAllLanguages, setUpdateAllLanguages] = useState(defaultUpdateAll)
  const [editUpdateAllLanguages, setEditUpdateAllLanguages] = useState(defaultEditUpdateAll)
  const lastTranslatedRef = useRef({})
  const lastEditTranslatedRef = useRef({})
  // Bumped whenever a form is reset or a different position is opened, so a
  // translation still in flight can tell its results are no longer wanted.
  const formGenerationRef = useRef(0)
  const editGenerationRef = useRef(0)

  const resetForm = () => {
    setJobTitles(defaultJobTitles)
    setLocation([])
    setTags([])
    setTagInput('')
    setDescriptions(defaultDescriptions)
    setError(defaultErrors)
    setActiveTab(0)
    setIsTranslating(false)
    setUpdateAllLanguages(defaultUpdateAll)
    lastTranslatedRef.current = {}
    formGenerationRef.current += 1
  }

  const closeModal = () => {
    resetForm()
    setOpen(false)
  }

  const addTag = () => {
    const value = tagInput.trim()
    if (value && !tags.includes(value)) {
      setTags((prevTags) => [...prevTags, value])
    }
    setTagInput('')
  }

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag()
    }
  }

  const removeTag = (tagToRemove) => setTags((prevTags) => prevTags.filter((tag) => tag !== tagToRemove))

  // Translate one tab's title/description into every OTHER language and write
  // the result into the given modal's state, overwriting whatever was there
  // before so all tabs stay in sync with the latest source text. Shared by the
  // create and edit modals; each passes its own state, setters and signature
  // cache. Does nothing unless that tab's "update all languages" box is
  // checked, which is what keeps a manual edit to a secondary language from
  // propagating to the rest.
  const syncOtherLanguages = async ({
    sourceLang,
    shouldUpdateAll,
    titles,
    descriptions: sourceDescriptions,
    setTitles,
    setDescriptions,
    signatureRef,
    setTranslating,
    generationRef
  }) => {
    if (!shouldUpdateAll) return

    const sourceTitle = titles[sourceLang]
    const sourceDescription = sourceDescriptions[sourceLang]

    if (!sourceTitle) return

    // JSON-encoded so a title/description boundary can't collide with a
    // plain-text separator (e.g. title="A B" + description="C" would
    // otherwise produce the same signature as title="A" + description="B C").
    const signature = JSON.stringify([sourceTitle, sourceDescription || ''])
    if (signatureRef.current[sourceLang] === signature) return

    // Claim the signature before the request starts (not after it resolves)
    // so a second blur firing before the first request completes sees the
    // claim and skips, instead of racing a duplicate call. Rolled back on
    // failure so the next blur can retry.
    signatureRef.current[sourceLang] = signature

    const generation = generationRef.current

    try {
      setTranslating(true)
      const response = await axios.post(
        `${APPLICANTS_API}/applicants/jobPosition/previewTranslation`,
        {
          source_lang: sourceLang,
          job_title: sourceTitle,
          description: sourceDescription || ''
        },
        { withCredentials: false }
      )

      // The form was reset or swapped to another position while this request
      // was open — writing the result now would repopulate the wrong form.
      if (generationRef.current !== generation) return

      if (response.data.success) {
        const translated = response.data.translations
        setTitles((prev) => {
          const next = { ...prev }
          langKeys.forEach((key) => {
            if (key !== sourceLang) next[key] = translated[key].job_title
          })
          return next
        })
        setDescriptions((prev) => {
          const next = { ...prev }
          langKeys.forEach((key) => {
            if (key !== sourceLang) next[key] = translated[key].description
          })
          return next
        })
      } else {
        signatureRef.current[sourceLang] = null
        toast.error(response.data.message ?? 'Përkthimi dështoi')
      }
    } catch (error) {
      signatureRef.current[sourceLang] = null
      if (generationRef.current !== generation) return
      toast.error(error.response?.data?.message ?? 'Ndodhi një gabim i papritur')
      console.log('error', error)
    } finally {
      if (generationRef.current === generation) setTranslating(false)
    }
  }

  // `shouldUpdateAll` is passed explicitly by the checkbox handlers, which know
  // the new value before the state update has been applied.
  const translateFromTab = (sourceLang, shouldUpdateAll = updateAllLanguages[sourceLang]) =>
    syncOtherLanguages({
      sourceLang,
      shouldUpdateAll,
      titles: jobTitles,
      descriptions,
      setTitles: setJobTitles,
      setDescriptions,
      signatureRef: lastTranslatedRef,
      setTranslating: setIsTranslating,
      generationRef: formGenerationRef
    })

  const translateFromEditTab = (sourceLang, shouldUpdateAll = editUpdateAllLanguages[sourceLang]) =>
    syncOtherLanguages({
      sourceLang,
      shouldUpdateAll,
      titles: editJobTitles,
      descriptions: editDescriptions,
      setTitles: setEditJobTitles,
      setDescriptions: setEditDescriptions,
      signatureRef: lastEditTranslatedRef,
      setTranslating: setIsEditTranslating,
      generationRef: editGenerationRef
    })

  // Hand-editing one tab can leave a checked source out of sync with it. Drop
  // the source's cached signature so its next blur re-translates instead of
  // short-circuiting on source text that hasn't itself changed.
  const invalidateSyncSignatures = (signatureRef, updateAll, editedLang) =>
    langKeys.forEach((key) => {
      if (key !== editedLang && updateAll[key]) signatureRef.current[key] = null
    })

  // At most one language may drive the others, so ticking a box unticks the
  // rest — two checked tabs would each overwrite the other on every blur.
  // Signatures are cleared too: the other tabs may have been edited by hand
  // while the box was off, so this tab must re-translate even when its own
  // text is unchanged since the last sync.
  const toggleUpdateAll = (key, checked) => {
    setUpdateAllLanguages((prev) => (checked ? { ...noLanguageSync(), [key]: true } : { ...prev, [key]: false }))
    if (checked) {
      lastTranslatedRef.current = {}
      translateFromTab(key, true)
    }
  }

  const toggleEditUpdateAll = (key, checked) => {
    setEditUpdateAllLanguages((prev) => (checked ? { ...noLanguageSync(), [key]: true } : { ...prev, [key]: false }))
    if (checked) {
      lastEditTranslatedRef.current = {}
      translateFromEditTab(key, true)
    }
  }

  // Tab switch just changes which tab is shown — re-translation is triggered
  // by onBlur on the title/description fields (which fires as focus leaves
  // them, including when the tab click moves focus away).
  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue)
  }

  const getJobPositions = async () => {
    try {
      const response = await axios.get(`${APPLICANTS_API}/applicants/jobPosition`, { withCredentials: false })
      setJobPositions(response.data.jobPositions)
    } catch (error) {
      toast.error(error.response?.data?.message)
      console.log('error', error)
    }
  }

  const addNewPosition = async (e) => {
    e.preventDefault()
    const newErrors = { ...defaultErrors, description: { ...defaultErrors.description } }

    try {
      await validationSchema.validate({ ...jobTitles, location, description: descriptions }, { abortEarly: false })

      const response = await axios.post(
        `${APPLICANTS_API}/applicants/jobPosition`,
        {
          translations: jobTitles,
          job_title: jobTitles.al,
          location,
          tags,
          description: descriptions
        },
        { withCredentials: false }
      )

      if (response.data.success) {
        const newJobPosition = response.data.newJobTitle
        setJobPositions((prevJobPositions) => [...prevJobPositions, newJobPosition])
        toast.success('Pozita u krijua me sukses!')
      }

      resetForm()
      setOpen(false)
    } catch (validationError) {
      if (validationError instanceof Yup.ValidationError) {
        validationError.inner.forEach((err) => {
          // Description errors have nested paths like "description.al"
          if (err.path.startsWith('description.')) {
            const lang = err.path.split('.')[1]
            newErrors.description[lang] = err.message
          } else {
            newErrors[err.path] = err.message
          }
        })
        setError(newErrors)

        // Surface the first language tab that has a title or description error
        const erroredTab = langKeys.findIndex((key) => newErrors[key] !== '' || newErrors.description[key] !== '')
        if (erroredTab !== -1) setActiveTab(erroredTab)
      } else {
        console.log('error', validationError)
      }
    }
  }

  const updatePosition = async (id, status) => {
    try {
      const response = await axios.put(
        `${APPLICANTS_API}/applicants/jobPosition?id=${id}`,
        {
          status
        },
        { withCredentials: false }
      )
      if (response.data.success) {
        const updatedJobPosition = response.data.jobPosition

        setJobPositions((prevJobPositions) =>
          prevJobPositions.map((position) => (position._id === updatedJobPosition._id ? updatedJobPosition : position))
        )
        toast.success('Statusi i pozites u ndryshua me sukses!')
      }
    } catch (error) {
      toast.error(error.response?.data?.message)
      console.log('error', error)
    }
  }

  const deletePosition = async (id) => {
    try {
      const response = await axios.delete(`${APPLICANTS_API}/applicants/jobPosition?id=${id}`, { withCredentials: false })
      if (response.data.success) {
        setJobPositions((prevJobPositions) => prevJobPositions.filter((position) => position._id !== id))
        toast.success('Pozita u fshij me sukses')
      }
    } catch (error) {
      toast.error(error.response?.data?.message)
      console.log('error', error)
    }
  }

  const openEditModal = (job) => {
    setEditingPosition(job)
    setEditJobTitles({ ...defaultJobTitles, ...job.translations })
    setEditLocation(Array.isArray(job.location) ? job.location : job.location ? [job.location] : [])
    setEditTags(job.tags || [])
    setEditTagInput('')
    setEditDescriptions({ ...defaultDescriptions, ...job.description })
    setEditError(defaultErrors)
    setEditActiveTab(0)
    setEditUpdateAllLanguages(defaultEditUpdateAll)
    setIsEditTranslating(false)
    lastEditTranslatedRef.current = {}
    editGenerationRef.current += 1
  }

  const closeEditModal = () => {
    setEditingPosition(null)
    setEditJobTitles(defaultJobTitles)
    setEditLocation([])
    setEditTags([])
    setEditTagInput('')
    setEditDescriptions(defaultDescriptions)
    setEditError(defaultErrors)
    setEditActiveTab(0)
    setEditUpdateAllLanguages(defaultEditUpdateAll)
    setIsEditTranslating(false)
    lastEditTranslatedRef.current = {}
    editGenerationRef.current += 1
  }

  const addEditTag = () => {
    const value = editTagInput.trim()
    if (value && !editTags.includes(value)) {
      setEditTags((prev) => [...prev, value])
    }
    setEditTagInput('')
  }

  const handleEditTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addEditTag()
    }
  }

  const removeEditTag = (tagToRemove) => setEditTags((prev) => prev.filter((t) => t !== tagToRemove))

  const saveEditPosition = async (e) => {
    e.preventDefault()
    const positionId = editingPosition._id
    const newErrors = { ...defaultErrors, description: { ...defaultErrors.description } }

    try {
      await validationSchema.validate(
        { ...editJobTitles, location: editLocation, description: editDescriptions },
        { abortEarly: false }
      )

      const response = await axios.put(
        `${APPLICANTS_API}/applicants/jobPosition?id=${positionId}`,
        {
          translations: editJobTitles,
          job_title: editJobTitles.al,
          location: editLocation,
          tags: editTags,
          description: editDescriptions
        },
        { withCredentials: false }
      )

      if (response.data.success) {
        const updated = response.data.jobPosition
        setJobPositions((prev) => prev.map((p) => (p._id === updated._id ? updated : p)))
        toast.success('Pozita u ndryshua me sukses!')
        closeEditModal()
      } else {
        toast.error(response.data.message ?? 'Ndryshimi dështoi')
      }
    } catch (validationError) {
      if (validationError instanceof Yup.ValidationError) {
        validationError.inner.forEach((err) => {
          if (err.path.startsWith('description.')) {
            const lang = err.path.split('.')[1]
            newErrors.description[lang] = err.message
          } else {
            newErrors[err.path] = err.message
          }
        })
        setEditError(newErrors)
        const erroredTab = langKeys.findIndex((key) => newErrors[key] !== '' || newErrors.description[key] !== '')
        if (erroredTab !== -1) setEditActiveTab(erroredTab)
      } else {
        toast.error(validationError.response?.data?.message ?? 'Ndodhi një gabim i papritur')
        console.log('error', validationError)
      }
    }
  }

  useEffect(() => {
    getJobPositions()
  }, [])

  const updateLanguageState = (e, key) => {
    setJobTitles({ ...jobTitles, [key]: e.target.value })
    invalidateSyncSignatures(lastTranslatedRef, updateAllLanguages, key)
  }

  const updateDescriptionState = (e, key) => {
    setDescriptions({ ...descriptions, [key]: e.target.value })
    invalidateSyncSignatures(lastTranslatedRef, updateAllLanguages, key)
  }

  const updateEditLanguageState = (e, key) => {
    setEditJobTitles({ ...editJobTitles, [key]: e.target.value })
    invalidateSyncSignatures(lastEditTranslatedRef, editUpdateAllLanguages, key)
  }

  const updateEditDescriptionState = (e, key) => {
    setEditDescriptions({ ...editDescriptions, [key]: e.target.value })
    invalidateSyncSignatures(lastEditTranslatedRef, editUpdateAllLanguages, key)
  }

  const openJobPositions = jobPositions.filter((item) => item.status)
  const closedJobPositions = jobPositions.filter((item) => !item.status)

  return (
    <div>
      <h3>Active positions</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {openJobPositions.length > 0
          ? openJobPositions.map(
              (oneJob) =>
                oneJob.status && (
                  <Position key={oneJob._id} job={oneJob} updatePosition={updatePosition} deletePosition={deletePosition} isActive={oneJob.status} onEdit={openEditModal} />
                )
            )
          : 'No Data'}
      </div>
      <div style={{ marginTop: '10px' }}>
        <h4>Inactive positions</h4>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              maxWidth: '70%'
            }}
          >
            {closedJobPositions.length > 0
              ? closedJobPositions.map(
                  (oneJob) =>
                    !oneJob.status && (
                      <Position key={oneJob._id} job={oneJob} updatePosition={updatePosition} deletePosition={deletePosition} isActive={oneJob.status} onEdit={openEditModal} />
                    )
                )
              : 'No Data'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button color='info' style={{ marginLeft: '10px' }} onClick={() => setOpen(true)}>
              Add new position
            </Button>
            <Modal
              open={open}
              onClose={closeModal}
              aria-labelledby='modal-modal-title'
              aria-describedby='modal-modal-description'
            >
              <form style={style} onSubmit={addNewPosition}>
                {/* Header */}
                <Box>
                  <Typography id='modal-modal-title' variant='h5' component='h2' sx={{ fontWeight: 600 }}>
                    Shto një pozitë të re
                  </Typography>
                  <Typography id='modal-modal-description' variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                    Zgjidh lokacionin, shto tags dhe plotëso titullin e përshkrimin për secilën gjuhë.
                  </Typography>
                </Box>

                {/* Shared details: location + tags */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth size='small' error={!!error.location}>
                    <InputLabel size='small' id='position-location-label'>
                      Lokacioni*
                    </InputLabel>
                    <Select
                      multiple
                      size='small'
                      labelId='position-location-label'
                      id='position-location'
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      input={<OutlinedInput label='Lokacioni*' />}
                      renderValue={(selected) => (
                        <Stack direction='row' spacing={0.5} sx={{ flexWrap: 'wrap', gap: '4px' }}>
                          {selected.map((val) => (
                            <Chip
                              key={val}
                              label={val}
                              size='small'
                              onDelete={() => setLocation(location.filter((l) => l !== val))}
                              onMouseDown={(e) => e.stopPropagation()}
                              color='default' sx={{ backgroundColor: '#e0e0e0 !important', borderRadius: '10px !important', color: '#000 !important' }}
                            />
                          ))}
                        </Stack>
                      )}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: '300px',
                            '& .MuiList-root': {
                              display: 'flex',
                              flexDirection: 'column'
                            },
                            '& .MuiMenuItem-root': {
                              border: '1px solid #e0e0e0',
                              margin: '4px 8px',
                              borderRadius: '4px',
                              padding: '8px 12px'
                            }
                          }
                        }
                      }}
                    >
                      {locations.map((name) => (
                        <MenuItem key={name} value={name}>
                          <ListItemText primary={name} />
                        </MenuItem>
                      ))}
                    </Select>
                    {!!error.location && <FormHelperText>{error.location}</FormHelperText>}
                  </FormControl>

                  <Box>
                    <TextField
                      fullWidth
                      size='small'
                      id='position-tags'
                      variant='outlined'
                      label='Tags'
                      placeholder='p.sh. Full-time, Remote...'
                      helperText={tags.length === 0 ? 'Shtyp Enter ose presje për të shtuar një tag' : ''}
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      onBlur={addTag}
                    />
                    {tags.length > 0 && (
                      <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', gap: '8px', mt: 1 }}>
                        {tags.map((tag) => (
                          <Chip key={tag} label={tag} size='small' onDelete={() => removeTag(tag)} color='default' sx={{ backgroundColor: '#e0e0e0 !important', borderRadius: '10px !important', color: '#000 !important' }} />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Box>

                <Divider />

                {/* Per-language title + description */}
                <Box>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant='fullWidth'
                    sx={{ minHeight: 40, mb: 1, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
                  >
                    {langKeys.map((key) => {
                      const hasError = error[key] !== '' || error.description[key] !== ''
                      return <Tab key={key} label={languages[key]} sx={hasError ? { color: 'error.main' } : undefined} />
                    })}
                  </Tabs>
                  {isTranslating && (
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
                      Duke plotësuar gjuhët e tjera automatikisht...
                    </Typography>
                  )}

                  {langKeys.map(
                    (key, index) =>
                      activeTab === index && (
                        <Box key={key} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <TextField
                            fullWidth
                            size='small'
                            error={error[key] !== ''}
                            helperText={error[key]}
                            id={`position-title-${key}`}
                            variant='outlined'
                            label={`Titulli (${languages[key]})*`}
                            value={jobTitles[key]}
                            onChange={(e) => updateLanguageState(e, key)}
                            onBlur={() => translateFromTab(key)}
                          />
                          <TextField
                            fullWidth
                            error={error.description[key] !== ''}
                            helperText={error.description[key]}
                            id={`position-description-${key}`}
                            variant='outlined'
                            label={`Përshkrimi (${languages[key]})*`}
                            value={descriptions[key]}
                            multiline
                            minRows={4}
                            onChange={(e) => updateDescriptionState(e, key)}
                            onBlur={() => translateFromTab(key)}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                size='small'
                                checked={updateAllLanguages[key]}
                                onChange={(e) => toggleUpdateAll(key, e.target.checked)}
                              />
                            }
                            label={
                              <Typography variant='body2'>
                                Përditëso të gjitha gjuhët
                                <Typography component='span' variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                                  Përkthen këtë tekst në të gjitha gjuhët e tjera dhe zëvendëson atë ekzistuese.
                                </Typography>
                              </Typography>
                            }
                            sx={{ alignItems: 'flex-start', mt: -0.5, '& .MuiCheckbox-root': { pt: 0.25 } }}
                          />
                        </Box>
                      )
                  )}
                </Box>

                <Divider />

                {/* Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button color='secondary' type='button' onClick={closeModal}>
                    Anulo
                  </Button>
                  <Button color='info' type='submit' disabled={isTranslating}>
                    Shto pozitën
                  </Button>
                </Box>
              </form>
            </Modal>
          </div>
        </div>
      </div>
      {/* Edit position modal */}
      <Modal open={!!editingPosition} onClose={closeEditModal}>
        <form style={style} onSubmit={saveEditPosition}>
          <Box>
            <Typography variant='h5' component='h2' sx={{ fontWeight: 600 }}>
              Ndrysho pozitën
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              Ndrysho lokacionin, tags dhe titullin e përshkrimin për secilën gjuhë.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size='small' error={!!editError.location}>
              <InputLabel size='small' id='edit-location-label'>
                Lokacioni*
              </InputLabel>
              <Select
                multiple
                size='small'
                labelId='edit-location-label'
                id='edit-location'
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
                input={<OutlinedInput label='Lokacioni*' />}
                renderValue={(selected) => (
                  <Stack direction='row' spacing={0.5} sx={{ flexWrap: 'wrap', gap: '4px' }}>
                    {selected.map((val) => (
                      <Chip
                        key={val}
                        label={val}
                        size='small'
                        onDelete={() => setEditLocation(editLocation.filter((l) => l !== val))}
                        onMouseDown={(e) => e.stopPropagation()}
                        color='default' sx={{ backgroundColor: '#e0e0e0 !important', borderRadius: '10px !important', color: '#000 !important' }}
                      />
                    ))}
                  </Stack>
                )}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: '300px',
                      '& .MuiList-root': {
                        display: 'flex',
                        flexDirection: 'column'
                      },
                      '& .MuiMenuItem-root': {
                        border: '1px solid #e0e0e0',
                        margin: '4px 8px',
                        borderRadius: '4px',
                        padding: '8px 12px'
                      }
                    }
                  }
                }}
              >
                {locations.map((name) => (
                  <MenuItem key={name} value={name}>
                    <ListItemText primary={name} />
                  </MenuItem>
                ))}
              </Select>
              {!!editError.location && <FormHelperText>{editError.location}</FormHelperText>}
            </FormControl>

            <Box>
              <TextField
                fullWidth
                size='small'
                variant='outlined'
                label='Tags'
                placeholder='p.sh. Full-time, Remote...'
                helperText={editTags.length === 0 ? 'Shtyp Enter ose presje për të shtuar një tag' : ''}
                value={editTagInput}
                onChange={(e) => setEditTagInput(e.target.value)}
                onKeyDown={handleEditTagKeyDown}
                onBlur={addEditTag}
              />
              {editTags.length > 0 && (
                <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', gap: '8px', mt: 1 }}>
                  {editTags.map((tag) => (
                    <Chip key={tag} label={tag} size='small' onDelete={() => removeEditTag(tag)} color='default' sx={{ backgroundColor: '#e0e0e0 !important', borderRadius: '10px !important', color: '#000 !important' }} />
                  ))}
                </Stack>
              )}
            </Box>
          </Box>

          <Divider />

          <Box>
            <Tabs
              value={editActiveTab}
              onChange={(_e, val) => setEditActiveTab(val)}
              variant='fullWidth'
              sx={{ minHeight: 40, mb: 2, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
            >
              {langKeys.map((key) => {
                const hasError = editError[key] !== '' || editError.description[key] !== ''
                return <Tab key={key} label={languages[key]} sx={hasError ? { color: 'error.main' } : undefined} />
              })}
            </Tabs>
            {isEditTranslating && (
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
                Duke plotësuar gjuhët e tjera automatikisht...
              </Typography>
            )}

            {langKeys.map(
              (key, index) =>
                editActiveTab === index && (
                  <Box key={key} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      size='small'
                      error={editError[key] !== ''}
                      helperText={editError[key]}
                      variant='outlined'
                      label={`Titulli (${languages[key]})*`}
                      value={editJobTitles[key]}
                      onChange={(e) => updateEditLanguageState(e, key)}
                      onBlur={() => translateFromEditTab(key)}
                    />
                    <TextField
                      fullWidth
                      error={editError.description[key] !== ''}
                      helperText={editError.description[key]}
                      variant='outlined'
                      label={`Përshkrimi (${languages[key]})*`}
                      value={editDescriptions[key]}
                      multiline
                      minRows={4}
                      onChange={(e) => updateEditDescriptionState(e, key)}
                      onBlur={() => translateFromEditTab(key)}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size='small'
                          checked={editUpdateAllLanguages[key]}
                          onChange={(e) => toggleEditUpdateAll(key, e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant='body2'>
                          Përditëso të gjitha gjuhët
                          <Typography component='span' variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                            Përkthen këtë tekst në të gjitha gjuhët e tjera dhe zëvendëson atë ekzistuese.
                          </Typography>
                        </Typography>
                      }
                      sx={{ alignItems: 'flex-start', mt: -0.5, '& .MuiCheckbox-root': { pt: 0.25 } }}
                    />
                  </Box>
                )
            )}
          </Box>

          <Divider />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button color='secondary' type='button' onClick={closeEditModal}>
              Anulo
            </Button>
            <Button color='info' type='submit' disabled={isEditTranslating}>
              Ruaj ndryshimet
            </Button>
          </Box>
        </form>
      </Modal>
    </div>
  )
}

export default OpenPosition
